import { ApiError, normalizeApiError } from '@enshield/api-client';
import type { EvidencePolicyDto, PublicClaimStatusDto } from '@enshield/domain';

export const ACCEPTED_EVIDENCE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'application/pdf'] as const;
export const EVIDENCE_MAX_BYTES = 10 * 1024 * 1024;
export const EVIDENCE_UPLOAD_TIMEOUT_MS = 30_000;
export const DEFAULT_EVIDENCE_POLICY: EvidencePolicyDto = {
  acceptedContentTypes: [...ACCEPTED_EVIDENCE_TYPES],
  maxFileBytes: EVIDENCE_MAX_BYTES,
  maxFilesPerClaim: 20,
  maxTotalBytesPerClaim: EVIDENCE_MAX_BYTES * 20,
};

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * The accepted types as a shopper would say them.
 *
 * `image/jpeg` is not an answer to "what can I upload"; "JPG" is.
 */
export function describeAcceptedTypes(policy = DEFAULT_EVIDENCE_POLICY): string {
  const labels = policy.acceptedContentTypes
    .map((type) => {
      const subtype = type.split('/')[1] ?? type;
      if (subtype === 'jpeg') return 'JPG';
      if (subtype === 'svg+xml') return 'SVG';
      return subtype.toUpperCase();
    })
    .filter((label, index, all) => all.indexOf(label) === index);
  if (labels.length === 0) return 'none';
  if (labels.length === 1) return labels[0]!;
  return `${labels.slice(0, -1).join(', ')}, or ${labels[labels.length - 1]}`;
}

export function validateEvidenceFile(file: File, policy = DEFAULT_EVIDENCE_POLICY): string | null {
  if (!file.size) return `“${file.name}” is empty and cannot be attached.`;
  if (file.size > policy.maxFileBytes) return `“${file.name}” is larger than the ${formatBytes(policy.maxFileBytes)} limit.`;
  if (file.type && !policy.acceptedContentTypes.includes(file.type)) {
    // Named from the POLICY, not from a hardcoded list. The message read
    // "JPG, PNG, GIF, WEBP, HEIC, or PDF" whatever the policy said, so it could
    // only ever be right by coincidence. (The accepted TYPES are currently
    // fixed platform-wide — `readEvidencePolicy` takes no store — so today the
    // two agree; the message is now derived rather than duplicated, which is
    // what stops them drifting when they stop being the same list.)
    return `Choose a supported file type: ${describeAcceptedTypes(policy)}.`;
  }
  return null;
}

export type EvidenceUploader = (path: string, form: FormData, onProgress: (percent: number) => void) => Promise<PublicClaimStatusDto>;

export interface EvidenceUploadTransportOptions {
  withCredentials?: boolean;
  headers?: Readonly<Record<string, string>>;
}

export function uploadEvidenceWithProgress<T>(
  absoluteUrl: string,
  form: FormData,
  onProgress: (percent: number) => void,
  options: EvidenceUploadTransportOptions = {},
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', absoluteUrl);
    xhr.timeout = EVIDENCE_UPLOAD_TIMEOUT_MS;
    xhr.withCredentials = options.withCredentials ?? false;
    for (const [name, value] of Object.entries(options.headers ?? {})) xhr.setRequestHeader(name, value);
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && event.total > 0) onProgress(Math.round((event.loaded / event.total) * 100));
    });
    xhr.addEventListener('load', () => {
      const requestId = typeof xhr.getResponseHeader === 'function' ? xhr.getResponseHeader('x-request-id') ?? undefined : undefined;
      let payload: unknown = {};
      try { payload = xhr.responseText ? JSON.parse(xhr.responseText) : {}; }
      catch {
        reject(new ApiError(xhr.status, { code: 'INVALID_RESPONSE', message: 'The server returned an unexpected response.' }, requestId));
        return;
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(normalizeApiError(xhr.status, payload, { code: 'UNKNOWN_ERROR', message: 'Unable to attach that file.' }, requestId));
        return;
      }
      resolve((typeof payload === 'object' && payload && 'data' in payload ? (payload as { data: T }).data : payload) as T);
    });
    xhr.addEventListener('error', () => reject(new ApiError(0, {
      code: 'NETWORK_ERROR',
      message: 'Could not reach the Enshield API. Check your connection and try again.',
    })));
    xhr.addEventListener('timeout', () => reject(new ApiError(0, {
      code: 'REQUEST_TIMEOUT',
      message: 'The evidence upload timed out. Check your connection and try again.',
    })));
    xhr.addEventListener('abort', () => reject(new ApiError(0, {
      code: 'REQUEST_ABORTED',
      message: 'The evidence upload was cancelled. Please try again.',
    })));
    xhr.send(form);
  });
}
